(ns ductile.ui.users
  (:require [clojure.string :as str]
            [ductile.scope-auth :as scope-auth]
            [ductile.ui.components :as ui]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :refer [tr]]
            [ductile.ui.router :as router]
            [ductile.ui.routes :as routes]
            [ductile.ui.scope]
            [ductile.ui.util :as util]
            [nextjournal.log :as log])
  (:require-macros [ductile.ui.scope.macros :refer [when-current-user-scope]]))

(defn role->scope-text [role]
  (let [scope->perms (get scope-auth/*role->scopes* role)]
    [:table
     [:thead [:tr.leading-6 [:th.text-left (tr "Scope")] [:th.w-20 (tr "Read")] [:th.w-20 (tr "Write")]]]
     (into [:tbody]
           (map (fn [[scope perms]]
                  [:tr.leading-5.border-t.border-indigo-300.first:border-t-0
                   [:td.text-left (name scope)]
                   [:td.text-center (when (perms :permission/read) "✓")]
                   [:td.text-center (when (perms :permission/write) "✓")]]))
           scope->perms)]))

(defn manage-all-users? []
  (util/has-required-scope? :scope/user-scopes))

(defn manage-compound-users? []
  (and (util/has-required-scope? :scope/compound-users)
       (not (manage-all-users?))))

(defn prepare-search-params [params]
  (cond-> params
    (= #{:no-compound} (:user/compounds params))
    (assoc :user/compounds nil)))

(defn search-event!
  {::deframe/event true}
  [{:keys [db]} params]
  (let [search-params (get-in db [:current-route/transient ::search-params])]
    {:db db
     ::deframe/call-lambda ['ductile.users/search
                            {:search-params (prepare-search-params search-params)}]}))

(defn search! [search-params]
  (deframe/call-lambda
   'ductile.users/search
   {:search-params (prepare-search-params search-params)}))

(defn dissoc-search-param! [k]
  (let [search-params (dissoc @(deframe/!route-get ::search-params) k)]
    (deframe/route-dissoc-in! [::search-params k])
    (search! search-params)))

(defn set-search-param! [k v]
  (if (str/blank? v)
    (dissoc-search-param! k)
    (let [search-params (assoc @(deframe/!route-get ::search-params) k v)]
      (deframe/route-assoc-in! [::search-params k] v)
      (search! search-params))))

(defn update-search-param! [k f v]
  (let [search-params @(deframe/!route-get ::search-params)
        v' (f (get search-params k) v)]
    (if (not-empty v')
      (do (deframe/route-assoc-in! [::search-params k] v')
          (search! (assoc search-params k v')))
      (dissoc-search-param! k))))

(def default-result-limit 100)

(defn menu-preview [user]
  (let [desktop-nav (routes/build-nav user)]
    (into [:div.flex.text-left.gap-2
           (when (empty? desktop-nav)
             [:span.opacity-60 (tr "None")])]
          (map (fn [item]
                 (let [{:as top :keys [label items]} (scope-auth/unwrap-protected-value item)]
                   [:div
                    [:span.font-bold.text-xs label]
                    (into [:ul.leading-tight {:class "text-[10px]"}]
                          (map (fn [item]
                                 (let [{:keys [label]} (scope-auth/unwrap-protected-value item)]
                                   [:li label])))
                          items)])))

          desktop-nav)))

(defn user-dashboard [{:keys [companies compounds]} {:keys [total-count email->user]}]
  (let [no-compound-selected? (contains? @(deframe/!route-get-in [::search-params :user/compounds]) :no-compound)
        {:user/keys [roles scopes email disabled?] company :company/name username :user/name :as search-params} @(deframe/!route-get ::search-params)]
    [:div.min-h-screen.flex.flex-col.lg:flex-row.gap-5
     [:div {:class "lg:w-[24%]"}
      [:div.flex.items-center.h-14.justify-between
       [:span.text-sm.font-bold.mr-1 (tr "Filters")]
       (when (some #(or (true? %) (seq %)) (vals search-params))
         [ui/button {:on-click #(do (set! (.-value (js/document.getElementById "email-input")) "")
                                    (set! (.-value (js/document.getElementById "username-input")) "")
                                    (deframe/route-dissoc! ::search-params)
                                    (search! {}))}
          (tr "Clear all")])]
      [:div.bg-gray-100.p-3.rounded.ring.ring-offset-1.ring-slate-200
       [:div.flex.items-center.mb-2
        [:div.text-xs.font-bold.text-gray-700.mr-2 (tr :user/name)]]
       [:div
        {:class ["flex" "flex-col" "gap-1"]}
        [ui/input
         {:type "text"
          :id "username-input"
          :on-change #(set-search-param! :user/name (.. % -target -value))}]]

       [:div.flex.items-center.mb-2.mt-4
        [:div.text-xs.font-bold.text-gray-700.mr-2 (tr :user/email)]]
       [:div
        {:class ["flex" "flex-col" "gap-1"]}
        [ui/input
         {:type "text"
          :id "email-input"
          :on-change #(set-search-param! :user/email (.. % -target -value))}]]
       [:div.flex.items-center.mb-2.mt-4
        [:div.text-xs.font-bold.text-gray-700.mr-2 (tr "Roles")]
        [ui/badge {:variant :white-green} (count roles)]]
       [:div.border-b.border-slate-200.my-4.mx-2]
       (into [:div]
             (map (fn [r]
                    (let [selected? (contains? roles r)]
                      [:div.flex.items-top.mb-2
                       [:label.text-xs.whitespace-pre-wrap.cursor-pointer.flex.items-center.hover:text-slate-600.justify-between.w-full
                        [:div.flex.items-center
                         [:input.mr-2.h-4.w-4 {:type "checkbox"
                                               :checked selected?
                                               :on-change #(update-search-param! :user/roles (if selected? (comp set disj) (comp set conj)) r)}]
                         (tr r)]
                        [:div.mx-1
                         [ui/tooltip {:variant :secondary
                                      :content [role->scope-text r]}
                          [:div.text-indigo-400 [ui/icon {:size 15} :info]]]]]])))
             (sort (if (manage-compound-users?)
                     scope-auth/roles-controllable-by-compound-manager
                     scope-auth/all-roles)))

       [:div.mt-5
        [:div.flex.items-center.mb-2.mt-4
         [:div.text-xs.font-bold.text-gray-700.mr-2 (tr "Company")]]
        [combobox/view
         {:choices (into [(combobox/->choice (tr "No company"))]
                         (map combobox/->choice)
                         companies)
          :value (combobox/->choice company)
          :on-change #(set-search-param! :company/name (:ui/value %))}]]
       [:div.mt-5
        [:div.flex.items-center.mb-2.mt-4
         [:div.text-xs.font-bold.text-gray-700.mr-2 (tr "User status")]
         [ui/badge {:variant :yellow} (count (:user/compounds search-params))]]
        [:div.mb-2
         [:label.text-xs.flex.items-center.cursor-pointer
          [:input.mr-2.h-4.w-4 {:type "checkbox"
                                :checked disabled?
                                :on-change (if disabled?
                                             #(do (deframe/route-dissoc-in! [::search-params :user/disabled?])
                                                  (search! (dissoc @(deframe/!route-get ::search-params)
                                                                   :user/disabled?)))
                                             #(set-search-param! :user/disabled? true))}]
          (tr "Show disabled")]]]
       [:div.mt-5
        [:div.flex.items-center.mb-2.mt-4
         [:div.text-xs.font-bold.text-gray-700.mr-2 (tr "Compounds")]
         [ui/badge {:variant :yellow} (count (:user/compounds search-params))]]
        (when-not (manage-compound-users?)
          [:div.mb-2
           [:label.text-xs.flex.items-center.cursor-pointer
            [:input.mr-2.h-4.w-4 {:type "checkbox"
                                  :checked no-compound-selected?
                                  :on-change (if no-compound-selected?
                                               #(update-search-param! :user/compounds disj :no-compound)
                                               #(set-search-param! :user/compounds #{:no-compound}))}]
            (tr "No compound")]])
        [:div.border-b.border-slate-200.my-4.mx-2]
        (into [:div]
              (map (fn [c]
                     (let [selected? (contains? @(deframe/!route-get-in [::search-params :user/compounds]) c)]
                       [:div.flex.items-center.mb-2
                        [:label.text-xs.cursor-pointer.flex.items-center
                         [:input.mr-2.h-4.w-4 {:type "checkbox"
                                               :checked selected?
                                               :on-change #(do
                                                             (when no-compound-selected?
                                                               (update-search-param! :user/compounds disj :no-compound))
                                                             (update-search-param! :user/compounds (if selected? (comp set disj) (comp set conj)) c))}]
                         c]])))
              compounds)]
       (when-current-user-scope
        :scope/user-scopes
        [:div.flex.items-center.mb-2.mt-4
         [:div.text-xs.font-bold.text-gray-700.mr-2 (tr "Scopes")]
         [ui/badge {:variant :secondary} (count scopes)]])
       (when-current-user-scope
        :scope/user-scopes
        (into [:div]
              (map (fn [s]
                     (let [selected? (contains? scopes s)]
                       [:div.flex.items-top.mb-2
                        [:label.text-xs.whitespace-pre-wrap.cursor-pointer.flex.items-center
                         [:input.mr-2.h-4.w-4 {:type "checkbox"
                                               :checked selected?
                                               :on-change #(update-search-param! :user/scopes (if selected? (comp set disj) (comp set conj)) s)}]
                         s]])))
              (sort scope-auth/all-scopes)))]]

     [:div.flex-grow.w-full
      [:div.flex.items-center.h-14
       [:span.text-sm.font-bold (tr "%1 users" total-count)]
       (when (> total-count default-result-limit)
         [:span.pl-4.text-slate-600.text-xs (tr "showing the first %1 results" default-result-limit)])
       (when (some #(or (true? %) (seq %)) (vals search-params))
         (into [:div.flex.ml-2.gap-1.flex-wrap]
               (concat (mapv (fn [o]
                               [ui/badge {:variant :secondary
                                          :on-close #(update-search-param! :user/scopes (comp set disj) o)}
                                o])
                             scopes)
                       (mapv (fn [o]
                               [ui/badge {:variant :white-green
                                          :on-close #(update-search-param! :user/roles (comp set disj) o)}
                                (tr o)])
                             roles)
                       (map (fn [o]
                              [ui/badge {:variant :yellow
                                         :on-close #(update-search-param! :user/compounds (comp set disj) o)}
                               (cond-> o (keyword? o) tr)])
                            (:user/compounds search-params))
                       (when-not (nil? disabled?)
                         [[ui/badge {:variant :white-green
                                     :on-close #(dissoc-search-param! :user/disabled?)}
                           (tr :user/disabled?)]])
                       (when-not (str/blank? username)
                         [[ui/badge {:variant :white-blue
                                     :on-close #(dissoc-search-param! :user/name)}
                           username]])
                       (when-not (str/blank? email)
                         [[ui/badge {:variant :orange
                                     :on-close #(dissoc-search-param! :user/email)}
                           email]])
                       (when-not (str/blank? company)
                         [[ui/badge {:variant :gray
                                     :on-close #(dissoc-search-param! :company/name)}
                           company]]))))
       [:div.flex.items-end.flex-grow
        [ui/button
         {:variant :primary
          :class ["ml-auto"]
          :href (router/path '[users new])}
         [ui/icon :plus]
         (tr "Add user")]]]
      (let [mobile? (ui/use-mobile? 950)]
        [:<> [ui/simple-table
              {:schema [[:details {:label (tr "User Details")}]
                        [:user/roles {:label [:div.flex.flex-wrap.flex-col.md:flex-row.gap-x-1
                                              [:div [:span.text-yellow-500 " • "] (tr "Compounds")]
                                              [:div [:span.text-green-500 " • "] (tr "Roles")]
                                              [:div [:span.text-indigo-500 " • "] (tr "Scopes")]]}]
                        :company/name
                        :actions]
               :mobile? mobile?
               :table (fn [row] {:class ["w-full rounded-md bg-white shadow-sm rounded-t ring ring-slate-200 ring-offset-1 border-separate"
                                         (when mobile? "text-sm cursor-pointer hover:ring-indigo-400 mb-4")]})
               :thead {:class (when-not mobile? "bg-slate-50 sticky top-0 text-left text-[13px] text-gray-500 uppercase tracking-wider shadow rounded")}
               :thead-tr {:class "rounded"}
               :th (fn [attr]
                     {:class
                      (ui/class-vec
                       "text-left font-medium "
                       (if mobile?
                         "p-1"
                         "p-3 uppercase text-gray-500"))})
               :tr {:class [(if mobile?
                              "odd:bg-white even:bg-slate-50"
                              "odd:bg-white even:bg-slate-50 hover:bg-indigo-200/20 border-b last:border-b-0")]}
               :td (fn [v row attr]
                     {:class (if mobile? "p-1" "p-2")})}

              (map (fn [{:user/keys [name email roles scopes company compounds disabled?] :as u}]
                     {:details [:div.flex.items-center.pl-1
                                (when (:oauth? u)
                                  [:div.h-3.w-3.mr-2 icons/microsoft])
                                (cond->> [:div.ml-1
                                          (when name [:div.font-bold.text-xs.mb-1 name])
                                          [:div.font-medium.text-gray-500.text-xs.flex
                                           email]]
                                  disabled?
                                  (vector :s))]
                      :user/roles [:div.flex.flex-col.gap-2
                                   (into [:div.flex.items-center.flex-wrap.gap-1.empty:hidden]
                                         (map (fn [c]
                                                [ui/tooltip
                                                 {:content [menu-preview {:user u :compound-name (:compound/name c)}]}
                                                 [:div [ui/badge {:variant :yellow
                                                                  :size (when mobile? 1)}
                                                        [:div.flex.gap-1
                                                         (:compound/name c)
                                                         [ui/icon {:size 14} :search]]]]]))
                                         (sort-by :compound/name compounds))

                                   (into [:div.flex.items-center.flex-wrap.gap-1.empty:hidden]
                                         (map (fn [role]
                                                (let [assigned? (contains? (set roles) role)]
                                                  (when assigned?
                                                    [ui/badge {:variant :white-green
                                                               :size (when mobile? 1)}
                                                     (tr role)]))))
                                         (sort (if (manage-compound-users?)
                                                 scope-auth/roles-controllable-by-compound-manager
                                                 scope-auth/all-roles)))

                                   (into [:div.flex.items-center.flex-wrap.gap-1.empty:hidden]
                                         (map (fn [scope]
                                                (let [assigned? (contains? (set scopes) scope)]
                                                  (when assigned?
                                                    [ui/badge {:variant :secondary
                                                               :size (when mobile? 1)}
                                                     scope]))))
                                         (sort scope-auth/all-scopes))]
                      :company/name [:span.text-left.text-xs.text-gray-500 (:company/name company)]
                      :actions
                      [:div.flex.flex-col.text-xs.font-bold.whitespace-no-wrap.items-start.pr-1
                       (when (util/has-required-scope? :scope/developer)
                         [:a.text-red-400.hover:text-red-700
                          {:href (str "/admin/impersonate/" (:ductile/id u))} "Impersonate"])
                       (when-current-user-scope
                        #{:scope/user-scopes :scope/compound-users}
                        [:a.text-gray-400.hover:text-gray-700.cursor-pointer
                         {:on-click #(ui/show-form-modal! 'ductile.users/show-user-edit-fields (select-keys u [:ductile/id :user/email]))}
                         (tr "Edit")])]})
                   (sort-by :user/email (vals email->user)))]
         (when (empty? email->user) [:div.text-center.font-bold.text-slate-600.border.border-slate-50.rounded-b.p-7.border-t-0.text-sm.bg-gray-100.shadow
                                     (tr "No users match your selected criteria") "."])])]]))

(defn render-user-dashboard [_result _match]
  [user-dashboard
   (-> @deframe/!current-route-fetch :result :options)
   (select-keys (:result @deframe/!current-route-fetch) [:email->user :total-count])])
